import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Clock3, ArrowRight, Copy, LifeBuoy, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { AppShell } from "@/components/app/AppShell";
import { libApi } from "@/lib/api";

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("pl-PL", { dateStyle: "medium", timeStyle: "short" });
  } catch (e) {
    return "";
  }
}

async function copyText(text) {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (e) { /* ignore */ }
  return false;
}

export default function RecentlyUsed() {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);

  useEffect(() => {
    let active = true;
    libApi.recentHistory().then((r) => active && setItems(r.history || [])).catch(() => active && setItems([]));
    return () => { active = false; };
  }, []);

  return (
    <AppShell>
      <div className="mx-auto max-w-content" data-testid="recent-root">
        <div className="flex items-center gap-2">
          <Clock3 size={22} strokeWidth={1.75} className="text-brand-accent" />
          <h1 className="font-display text-3xl font-semibold text-brand-text sm:text-4xl">Ostatnio używane</h1>
        </div>
        <p className="mt-2 text-brand-sec">Szybki powrót do promptów, z których niedawno korzystałeś.</p>

        {items === null ? (
          <div className="mt-6 space-y-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="h-24 animate-pulse rounded-card bg-brand-surface" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <div data-testid="recent-empty" className="mt-6 rounded-card border border-brand-line bg-brand-surface px-5 py-8 text-brand-sec">
            Historia jest pusta. Pojawi się tutaj, gdy zapiszesz pierwszą wersję promptu.
          </div>
        ) : (
          <ul className="mt-6 space-y-3" data-testid="recent-list">
            {items.map((h) => (
              <li key={h.id} data-testid="recent-card" className="rounded-card border border-brand-line bg-brand-surface p-4 sm:p-5">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 text-xs font-medium text-brand-muted">
                      {h.variant === "rescue" ? (
                        <span className="inline-flex items-center gap-1 text-brand-accent"><LifeBuoy size={12} /> Ratunkowy</span>
                      ) : (
                        <span className="inline-flex items-center gap-1"><Sparkles size={12} /> Standard</span>
                      )}
                      <span>·</span>
                      {formatDate(h.created_at)}
                    </div>
                    <h3 className="mt-1 font-display text-[15px] font-semibold text-brand-text">{h.prompt_name || h.prompt_slug}</h3>
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      data-testid="recent-copy"
                      onClick={async () => (await copyText(h.compiled_text)) && toast.success("Skopiowano z historii.")}
                      className="inline-flex items-center gap-1.5 rounded-field border border-brand-line px-3 py-2 text-xs font-semibold text-brand-text transition-colors hover:border-brand-accent/60"
                    >
                      <Copy size={13} strokeWidth={2} /> Kopiuj
                    </button>
                    <button
                      type="button"
                      data-testid="recent-open"
                      onClick={() => navigate(`/prompt/${h.prompt_slug}`, { state: { values: h.values, prefill: true } })}
                      className="inline-flex items-center gap-1.5 rounded-field bg-brand-accent px-3 py-2 text-xs font-semibold text-brand-on transition-transform hover:-translate-y-0.5"
                    >
                      Otwórz <ArrowRight size={13} strokeWidth={2} />
                    </button>
                  </div>
                </div>
                <p className="mt-2 line-clamp-2 text-[13px] text-brand-sec">{h.compiled_text}</p>
              </li>
            ))}
          </ul>
        )}
      </div>
    </AppShell>
  );
}
